import { useEffect, useRef, useState } from 'react';
import { ArrowsDownUp, CheckCircle } from '@phosphor-icons/react';
import { getAsset, compareAmounts, type AssetCode, type SwapQuoteDTO } from '@onyx/shared';
import { useWallet, useMoneyMutation, postSwapQuote } from '../lib/queries';
import { availableOf, useFiat } from '../lib/app';
import { useLang, useT, errorText } from '../lib/i18n';
import { cleanAmountInput, displayDigits, formatCrypto, formatFiat, formatRate } from '../lib/format';
import { api, ApiError } from '../lib/api';
import { haptic } from '../lib/telegram';
import { Facts, Notice, Screen, ScreenHeader, Slab, toast } from '../components/ui';
import { AssetSwitch } from './Send';

const REFRESH_MS = 15_000;

export function Swap({ initialFrom, initialTo }: { initialFrom?: AssetCode; initialTo?: AssetCode }) {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const wallet = useWallet();
  const firstHeld = wallet.data?.balances.find((b) => Number(b.available) > 0)?.asset;
  const [from, setFrom] = useState<AssetCode>(initialFrom ?? firstHeld ?? 'USDT');
  const [to, setTo] = useState<AssetCode>(initialTo ?? (from === 'TON' ? 'USDT' : 'TON'));
  const [side, setSide] = useState<'from' | 'to'>('from');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [quote, setQuote] = useState<SwapQuoteDTO | null>(null);
  const [quoteErr, setQuoteErr] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [done, setDone] = useState<{ give: string; get: string } | null>(null);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);
  const fromDef = getAsset(from)!;
  const toDef = getAsset(to)!;
  const available = availableOf(wallet.data, from);
  const typed = side === 'from' ? fromAmount : toAmount;

  const execute = useMoneyMutation((quoteId: string) => api<{ fromAmount: string; toAmount: string }>('/swap', { body: { quoteId } }));

  // Quote follows typing (debounced) and refreshes before the server-side TTL runs out.
  useEffect(() => {
    setQuoteErr(null);
    if (!typed || Number(typed) <= 0) {
      setQuote(null);
      if (side === 'from') setToAmount('');
      else setFromAmount('');
      return;
    }
    const run = () => {
      const id = ++seq.current;
      postSwapQuote({ from, to, amount: typed, side })
        .then((q) => {
          if (id !== seq.current) return;
          setQuote(q);
          if (side === 'from') setToAmount(q.toAmount);
          else setFromAmount(q.fromAmount);
        })
        .catch((e) => {
          if (id !== seq.current) return;
          setQuote(null);
          setQuoteErr(errorText(t, e instanceof ApiError ? e.code : undefined));
        });
    };
    const debounce = setTimeout(run, 320);
    const refresh = setInterval(run, REFRESH_MS);
    return () => {
      clearTimeout(debounce);
      clearInterval(refresh);
    };
  }, [from, to, typed, side, t, nonce]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const prices = wallet.data?.prices;

  const over = !!fromAmount && compareAmounts(fromAmount, available) > 0;
  const secondsLeft = quote ? Math.max(0, Math.ceil((Math.min(Date.parse(quote.expiresAt), now + REFRESH_MS) - now) / 1000)) : 0;
  const canSwap = !!quote && !over && Number(fromAmount) > 0;
  const feeShare = Number(quote?.feePercent ?? '0.5') / 100;
  const feePercentText = (quote?.feePercent ?? '0.5').replace('.', lang === 'ru' ? ',' : '.');

  const flip = () => {
    haptic.select();
    setFrom(to);
    setTo(from);
    setSide('from');
    setFromAmount(toAmount ? cleanAmountInput(toAmount, toDef.decimals) : '');
    setToAmount('');
  };

  const pick = (which: 'from' | 'to', a: AssetCode) => {
    if (which === 'from') {
      if (a === to) setTo(from);
      setFrom(a);
    } else {
      if (a === from) setFrom(to);
      setTo(a);
    }
  };

  if (done) {
    return (
      <Screen className="done" footer={<Slab onClick={() => { setDone(null); setFromAmount(''); setToAmount(''); setQuote(null); }}>{t['common.done']}</Slab>}>
        <div className="done__body">
          <span className="done__mark">
            <CheckCircle size={56} weight="fill" aria-hidden="true" />
          </span>
          <h1>{t['swap.doneTitle']}</h1>
          <p className="num">
            {done.give} {from} → {done.get} {to}
          </p>
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      className="swap"
      footer={
        <Slab
          disabled={!canSwap}
          loading={execute.isPending}
          onClick={() =>
            quote &&
            execute.mutate(quote.id, {
              onSuccess: (r) => {
                haptic.success();
                setDone({ give: formatCrypto(r.fromAmount, fromDef.decimals, lang), get: formatCrypto(r.toAmount, toDef.decimals, lang) });
              },
              onError: (e) => {
                haptic.error();
                const code = e instanceof ApiError ? e.code : undefined;
                if (code === 'quote_expired') {
                  toast.info(t['swap.expired']);
                  setNonce((n) => n + 1);
                } else toast.bad(errorText(t, code));
              },
            })
          }
        >
          {quote ? `${t['swap.submit']} ${formatCrypto(quote.fromAmount, fromDef.decimals, lang)} ${from} → ${formatCrypto(quote.toAmount, 4, lang)} ${to}` : t['swap.submit']}
        </Slab>
      }
    >
      <ScreenHeader title={t['swap.title']} onBack={false} />


      <div className="swap__pair">
        <div className={`swap__leg${over ? ' is-invalid' : ''}`}>
          <div className="swap__leg-head">
            <span className="swap__leg-label">{t['swap.give']}</span>
            <button type="button" className="tool-btn tool-btn--solid" onClick={() => { setSide('from'); setFromAmount(available); }}>
              {t['withdraw.max']}
            </button>
          </div>
          <div className="swap__leg-body">
            <AssetSwitch asset={from} onPick={(a) => pick('from', a)} />
            <input
              className="swap__input num"
              inputMode="decimal"
              aria-label={t['swap.give']}
              placeholder="0"
              value={side === 'to' && fromAmount ? formatCrypto(fromAmount, displayDigits(Number(fromAmount), fromDef.stable), lang).replace(/\u2009/g, '') : lang === 'ru' ? fromAmount.replace('.', ',') : fromAmount}
              onChange={(e) => {
                setSide('from');
                setFromAmount(cleanAmountInput(e.target.value, fromDef.decimals));
              }}
            />
          </div>
          {over && <p className="field__error">{t['err.insufficient_funds']}</p>}
        </div>

        <button type="button" className="swap__flip" onClick={flip} aria-label={t['swap.flip']}>
          <ArrowsDownUp size={20} weight="bold" />
        </button>

        <div className="swap__leg">
          <div className="swap__leg-head">
            <span className="swap__leg-label">{t['swap.get']}</span>
          </div>
          <div className="swap__leg-body">
            <AssetSwitch asset={to} onPick={(a) => pick('to', a)} onlyHeld={false} />
            <input
              className="swap__input num"
              inputMode="decimal"
              aria-label={t['swap.get']}
              placeholder="0"
              value={side === 'from' && toAmount ? formatCrypto(toAmount, displayDigits(Number(toAmount), toDef.stable), lang).replace(/\u2009/g, '') : lang === 'ru' ? toAmount.replace('.', ',') : toAmount}
              onChange={(e) => {
                setSide('to');
                setToAmount(cleanAmountInput(e.target.value, toDef.decimals));
              }}
            />
          </div>
        </div>
      </div>

      {quoteErr && <Notice tone="bad">{quoteErr}</Notice>}

      <Facts
        items={[
          {
            label: t['swap.market'],
            value: prices ? <span className="num">1 {from} = {formatRate(quote ? Number(quote.rate) / (1 - feeShare) : prices[from].USD / prices[to].USD, lang)} {to}</span> : '—',
          },
          {
            label: t['swap.fee'],
            value: (
              <span className="num">
                {feePercentText}%
                {quote && ` · ${formatCrypto((Number(quote.fromAmount) * feeShare).toFixed(fromDef.decimals), displayDigits(Number(quote.fromAmount) * feeShare, fromDef.stable), lang)} ${from}`}
              </span>
            ),
          },
          quote && { label: t['swap.yourRate'], value: <span className="num">1 {from} = {formatRate(Number(quote.rate), lang)} {to}</span>, strong: true },
          quote && { label: t['swap.value'], value: <span className="num">{formatFiat(Number(quote.fromAmount) * (prices?.[from][fiat] ?? 0), fiat, lang)}</span> },
        ]}
      />
      {quote && <p className="swap__timer num">{t['swap.refresh'](secondsLeft)}</p>}
    </Screen>
  );
}
